// src/pages/Licenciamento/LicenciamentoPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Box,
    Typography,
    TextField,
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Card,
    CardContent,
    CircularProgress,
    Autocomplete,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    Tooltip,
    Snackbar,
    Alert,
    Stack,
    Divider,
    Paper,
    Chip,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import StepConnector, {
    stepConnectorClasses,
} from '@mui/material/StepConnector';
import * as Lucide from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { encryptData } from './crypto-utils';
import { useBootstrap } from '../../context/BootstrapContext';
import { gerarSecretIot } from '../../infra/api/SecretsIot';

const SERVICE_UUID = '12345678-1234-5678-1234-56789abcdef0';
const CHARACTERISTIC_UUID = 'abcd1234-5678-90ab-cdef-1234567890ab';

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: { top: 22 },
    [`& .${stepConnectorClasses.line}`]: {
        height: 3,
        border: 0,
        backgroundColor: theme.palette.divider,
        borderRadius: 1,
    },
}));

function ColorlibStepIcon({ active, completed }) {
    const theme = useTheme();
    return completed ? (
        <Lucide.CircleCheck color={theme.palette.success.main} />
    ) : (
        <Lucide.Circle color={active ? theme.palette.primary.main : theme.palette.action.disabled} />
    );
}

const isIOS = () => {
    return (
        [
            'iPad Simulator',
            'iPhone Simulator',
            'iPod Simulator',
            'iPad',
            'iPhone',
            'iPod',
        ].includes(navigator.platform) ||
        (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    );
};

export default function ProvisioningPage() {
    const navigate = useNavigate();
    const { tenant } = useParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const {
        bootstrap,
        loading: bootLoading,
        error: bootError,
    } = useBootstrap();

    const steps = ['Conectar', 'Provisionar'];
    const notificationBuffer = useRef('');

    const resolvedClientId = bootstrap?.client?.id || '';
    const resolvedClientName = bootstrap?.client?.name || '';

    const resolvedTenant = bootstrap?.tenant || tenant;

    const [step, setStep] = useState(0);
    const [selectedNetwork, setSelectedNetwork] = useState(null);
    const [password, setPassword] = useState('');
    const [showPwd, setShowPwd] = useState(false);
    const [includeCode, setIncludeCode] = useState(false);

    const [accessKey, setAccessKey] = useState('');
    const [showAccessKey, setShowAccessKey] = useState(false);
    const [generatingKey, setGeneratingKey] = useState(false);

    const [connecting, setConnecting] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [sending, setSending] = useState(false);
    const [networks, setNetworks] = useState([]);
    const scanTimeout = useRef(null);

    const [device, setDevice] = useState(null);
    const [characteristic, setCharacteristic] = useState(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [statusLog, setStatusLog] = useState([]);
    const [provisioningComplete, setProvisioningComplete] = useState(false);

    const [helpOpen, setHelpOpen] = useState(false);

    const [errorMsg, setErrorMsg] = useState('');
    const [fieldErrors, setFieldErrors] = useState({
        ssid: '',
        password: '',
        accessKey: '',
    });

    const [openAC, setOpenAC] = useState(false);
    const [runningOnIOS] = useState(isIOS);

    const log = (level, msg) => {
        console[level === 'error' ? 'error' : 'log'](
            `[${level.toUpperCase()}] ${msg}`
        );
        setStatusLog((lines) => [...lines, { level, time: new Date(), msg }]);
    };

    const processNotificationBuffer = () => {
        let buffer = notificationBuffer.current;
        while (buffer.length > 0) {
            const startIndex = buffer.indexOf('{');
            if (startIndex === -1) {
                buffer = '';
                break;
            }
            if (startIndex > 0) buffer = buffer.substring(startIndex);

            let braceCount = 0;
            let endIndex = -1;
            for (let i = 0; i < buffer.length; i++) {
                if (buffer[i] === '{') braceCount++;
                else if (buffer[i] === '}') braceCount--;
                if (braceCount === 0) {
                    endIndex = i;
                    break;
                }
            }
            if (endIndex !== -1) {
                const jsonString = buffer.substring(0, endIndex + 1);
                try {
                    const jsonData = JSON.parse(jsonString);
                    setTimeout(() => handleJsonMessage(jsonData), 0);
                    buffer = buffer.substring(endIndex + 1);
                } catch (e) {
                    console.error(
                        'Erro ao processar JSON, descartando segmento:',
                        jsonString,
                        e
                    );
                    buffer = buffer.substring(1);
                }
            } else {
                break;
            }
        }
        notificationBuffer.current = buffer;
    };

    const handleJsonMessage = (jsonData) => {
        if (jsonData.networks && Array.isArray(jsonData.networks)) {
            log(
                'info',
                `📡 Recebido lote de ${jsonData.networks.length} redes...`
            );
            setNetworks((prevNetworks) => {
                const combinedNetworks = [
                    ...prevNetworks,
                    ...jsonData.networks,
                ];
                const networkMap = new Map();
                for (const network of combinedNetworks) {
                    if (
                        !networkMap.has(network.ssid) ||
                        network.rssi > networkMap.get(network.ssid).rssi
                    ) {
                        networkMap.set(network.ssid, network);
                    }
                }
                return Array.from(networkMap.values()).sort(
                    (a, b) => b.rssi - a.rssi
                );
            });
            setOpenAC(true);
        } else if (jsonData.status) {
            switch (jsonData.status) {
                case 'SCAN_DONE':
                    log('info', '⏱️ Scan finalizado pelo dispositivo.');
                    clearTimeout(scanTimeout.current);
                    setScanning(false);
                    setOpenAC(true);
                    break;
                case 'CONNECTED':
                    setSending(false);
                    log(
                        'success',
                        `✅ ${jsonData.message || 'Conectado com sucesso!'}`
                    );
                    setProvisioningComplete(true);
                    break;
                case 'AUTH_ERROR':
                    setSending(false);
                    log(
                        'error',
                        `❌ ${jsonData.message || 'Erro de autenticação.'}`
                    );
                    setErrorMsg(jsonData.message || 'Erro, tente novamente.');
                    break;
                default:
                    log('warning', `Status desconhecido: ${jsonData.status}`);
            }
        }
    };

    const onNotify = (event) => {
        const textChunk = new TextDecoder().decode(event.target.value);
        notificationBuffer.current += textChunk;
        processNotificationBuffer();
    };

    const setupChar = async (dev) => {
        log('info', 'Passo 1: Conectando ao servidor GATT...');
        const server = await dev.gatt.connect();
        log('success', 'Passo 1: Conectado ao servidor GATT.');
        log(
            'info',
            `Passo 2: Obtendo serviço primário com UUID: ${SERVICE_UUID}`
        );
        const svc = await server.getPrimaryService(SERVICE_UUID);
        log('success', 'Passo 2: Serviço primário obtido.');
        log(
            'info',
            `Passo 3: Obtendo característica com UUID: ${CHARACTERISTIC_UUID}`
        );
        const char = await svc.getCharacteristic(CHARACTERISTIC_UUID);
        log('success', 'Passo 3: Característica obtida.');
        log('info', 'Passo 4: Habilitando comunicação (indicate/notify)...');
        await char.startNotifications();
        log('success', 'Passo 4: Comunicação habilitada.');
        char.addEventListener('characteristicvaluechanged', onNotify);
        log('info', 'Setup do BLE concluído com sucesso.');
        return char;
    };

    const handleConnect = async () => {
        if (!navigator.bluetooth) {
            setErrorMsg('Navegador não suporta Web Bluetooth.');
            return;
        }
        setConnecting(true);
        setStatusLog([]);
        log('info', '🛰️ Solicitando dispositivo BLE…');
        try {
            const dev = await navigator.bluetooth.requestDevice({
                filters: [{ namePrefix: 'AUFTEK_', services: [SERVICE_UUID] }],
            });
            setDevice(dev);
            log('info', `🔗 Conectando a ${dev.name || dev.id}…`);
            const c = await setupChar(dev);
            setCharacteristic(c);
            log('success', '✅ Dispositivo conectado e pronto para uso.');
            setStep(1);
        } catch (e) {
            log('error', `❌ Falha no setup do BLE: ${e.message}`);
            setErrorMsg(
                `Erro: ${e.message}. Tente limpar o cache do Bluetooth.`
            );
        } finally {
            setConnecting(false);
        }
    };

    const handleGenerateKey = async () => {
        if (!resolvedTenant) {
            setErrorMsg(
                'Tenant não identificado. Verifique a configuração do seu ambiente.'
            );
            return;
        }
        if (!resolvedClientId) {
            setErrorMsg(
                'Cliente não carregado do bootstrap. Tente recarregar a página.'
            );
            return;
        }

        setGeneratingKey(true);
        setFieldErrors((prev) => ({ ...prev, accessKey: '' }));

        try {
            const novaChave = await gerarSecretIot(
                resolvedTenant,
                resolvedClientId
            );

            if (typeof novaChave === 'string' && novaChave.trim() !== '') {
                setAccessKey(novaChave);
            } else {
                throw new Error(
                    'Formato de resposta inválido ao gerar a chave.'
                );
            }
        } catch (error) {
            setErrorMsg(error.message || 'Erro ao gerar Access Key.');
        } finally {
            setGeneratingKey(false);
        }
    };

    const sendBleJson = async (jsonData) => {
        if (!characteristic) {
            log('warning', '⚠️ Conecte primeiro');
            return;
        }
        const bytes = new TextEncoder().encode(JSON.stringify(jsonData));
        try {
            await characteristic.writeValueWithoutResponse(bytes);
        } catch (e) {
            log('error', `❌ Erro ao enviar comando: ${e.message}`);
            throw e;
        }
    };

    const handleScan = () => {
        setSelectedNetwork(null);
        setNetworks([]);
        setScanning(true);
        setOpenAC(true);
        log('info', '🔍 Iniciando scan de redes…');
        clearTimeout(scanTimeout.current);
        scanTimeout.current = setTimeout(() => {
            setScanning((currentScanning) => {
                if (currentScanning) {
                    log(
                        'warning',
                        '⏱️ Timeout no scan. Nenhuma rede recebida.'
                    );
                    return false;
                }
                return currentScanning;
            });
        }, 15000);
        sendBleJson({ comando: 'SCAN' }).catch(() => setScanning(false));
    };

    const handleSend = async () => {
        const errs = { ssid: '', password: '', accessKey: '' };
        if (!selectedNetwork) errs.ssid = 'Selecione uma rede da lista';
        if (!password) errs.password = 'Senha obrigatória';
        if (!accessKey.trim())
            errs.accessKey = 'Access Key é obrigatória. Clique em Gerar.';

        setFieldErrors(errs);
        if (errs.ssid || errs.password || errs.accessKey) return;

        if (!resolvedClientId) {
            setErrorMsg(
                'Cliente não carregado do bootstrap. Tente recarregar a página.'
            );
            return;
        }
        if (!device || !device.gatt) {
            setErrorMsg(
                'Nenhum dispositivo conectado. Conecte via BLE antes de enviar.'
            );
            return;
        }

        setStatusLog([]);
        setProvisioningComplete(false);
        setModalOpen(true);
        setSending(true);
        log('info', '📤 Criptografando e enviando payload completo…');

        const fullProvisionData = {
            comando: 'PROVISION',
            pop: 'auftek2025',
            clientId: resolvedClientId,
            accessKey: accessKey.trim(),
            ssid: selectedNetwork.ssid,
            password: password,
            propagate: includeCode,
        };

        const { ciphertext, iv } = await encryptData(
            JSON.stringify(fullProvisionData)
        );
        const blePayload = { data_c: ciphertext, iv: iv };

        try {
            if (!device.gatt.connected) {
                log('info', '🔄 Reconectando BLE…');
                const c = await setupChar(device);
                setCharacteristic(c);
            }
            await sendBleJson(blePayload);
            log('info', '⌛ Aguardando resposta do dispositivo…');
        } catch (e) {
            setSending(false);
        }
    };

    useEffect(() => () => clearTimeout(scanTimeout.current), []);

    const back = () => {
        setStep(0);
        setNetworks([]);
        setSelectedNetwork(null);
        setOpenAC(false);
        if (device && device.gatt?.connected) {
            device.gatt.disconnect();
            log('info', '🔌 Dispositivo desconectado.');
        }
        setDevice(null);
        setCharacteristic(null);
    };

    const getSignalColor = (rssi) => {
        if (rssi >= -60) return 'success.main';
        if (rssi >= -80) return 'warning.main';
        return 'error.main';
    };

    const getSignalColorSemantic = (rssi) => {
        if (rssi >= -60) return 'success';
        if (rssi >= -80) return 'warning';
        return 'error';
    };

    const getSignalIcon = (rssi) =>
        rssi >= -50 ? (
            <Lucide.Wifi />
        ) : rssi >= -60 ? (
            <Lucide.SignalHigh />
        ) : rssi >= -70 ? (
            <Lucide.SignalMedium />
        ) : rssi >= -80 ? (
            <Lucide.SignalLow />
        ) : (
            <Lucide.SignalZero />
        );

    return (
        <Container
            maxWidth="sm"
            disableGutters={isMobile}
            sx={{ pt: { xs: 0, sm: 6 }, pb: { xs: 4, sm: 6 } }}
        >
            {!navigator.bluetooth && !runningOnIOS && (
                <Alert
                    severity="warning"
                    sx={{
                        mb: 2,
                        mx: { xs: 2, sm: 0 },
                        borderRadius: { xs: 2, sm: 2 },
                    }}
                >
                    Este navegador não suporta Web Bluetooth. Use Chrome ou Edge
                    mais recente.
                </Alert>
            )}

            {runningOnIOS && (
                <Alert
                    severity="error"
                    sx={{
                        mb: 2,
                        mx: { xs: 2, sm: 0 },
                        borderRadius: { xs: 2, sm: 2 },
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        sx={{ mb: 0.5 }}
                    >
                        Plataforma Incompatível
                    </Typography>
                    <Typography variant="body2">
                        O provisionamento por Bluetooth não é suportado em
                        dispositivos Apple (iPhone/iPad).
                        <br />
                        Utilize um Computador (com Chrome/Edge) ou um
                        Dispositivo Android.
                    </Typography>
                </Alert>
            )}

            {!!bootError && (
                <Alert
                    severity="warning"
                    sx={{
                        mb: 2,
                        mx: { xs: 2, sm: 0 },
                        borderRadius: { xs: 2, sm: 2 },
                    }}
                >
                    Não foi possível carregar dados do cliente (bootstrap).
                    Verifique sua sessão/tenant.
                </Alert>
            )}

            <Card
                sx={{
                    borderRadius: { xs: 0, sm: 3 },
                    boxShadow: {
                        xs: 'none',
                        sm: '0 4px 20px rgba(0,0,0,0.05)',
                    },
                    bgcolor: {
                        xs: 'background.default',
                        sm: 'background.paper',
                    },
                }}
            >
                <CardContent
                    sx={{
                        p: { xs: 2, sm: 4 },
                        '&:last-child': { pb: { xs: 3, sm: 4 } },
                    }}
                >
                    <Stack spacing={{ xs: 2, sm: 3 }}>
                        {/* HEADER DA PÁGINA DE PROVISIONAMENTO COM BOTÃO DE AJUDA */}
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Box display="inline-flex" alignItems="center">
                                <Lucide.Wifi
                                    size={32}
                                    color={theme.palette.primary.main}
                                    style={{ marginRight: 8 }}
                                />
                                <Box>
                                    <Typography
                                        variant={isMobile ? 'h6' : 'h5'}
                                        component="h2"
                                        fontWeight="bold"
                                        lineHeight={1.2}
                                    >
                                        Provisionamento
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        Conecte via BLE e envie as credenciais
                                    </Typography>
                                </Box>
                            </Box>
                            <Tooltip title="Como funciona o provisionamento?">
                                <IconButton
                                    color="primary"
                                    onClick={() => setHelpOpen(true)}
                                    sx={{
                                        bgcolor: alpha(
                                            theme.palette.primary.main,
                                            0.05
                                        ),
                                    }}
                                >
                                    <Lucide.HelpCircle />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        <Divider sx={{ mt: { xs: 0.5, sm: 1 }, mb: 1 }} />

                        <Stepper
                            alternativeLabel
                            activeStep={step}
                            connector={<ColorlibConnector />}
                        >
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel
                                        StepIconComponent={ColorlibStepIcon}
                                    >
                                        <Typography
                                            fontWeight="500"
                                            fontSize={
                                                isMobile ? '0.85rem' : '1rem'
                                            }
                                        >
                                            {label}
                                        </Typography>
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>

                        {step === 0 && (
                            <Box textAlign="center" pt={2} pb={1}>
                                <Button
                                    variant="contained"
                                    onClick={handleConnect}
                                    size="medium"
                                    fullWidth={isMobile}
                                    sx={{
                                        borderRadius: 2,
                                        px: { sm: 4 },
                                        py: { xs: 1.2, sm: 1.5 },
                                    }}
                                    startIcon={
                                        connecting ? (
                                            <CircularProgress
                                                size={20}
                                                color="inherit"
                                            />
                                        ) : (
                                            <Lucide.Wifi />
                                        )
                                    }
                                    disabled={
                                        connecting ||
                                        bootLoading ||
                                        runningOnIOS
                                    }
                                >
                                    {connecting
                                        ? 'A Conectar...'
                                        : 'Conectar Dispositivo'}
                                </Button>
                            </Box>
                        )}

                        {step === 1 && (
                            <Stack spacing={{ xs: 1.5, sm: 2.5 }}>
                                {/* Substituição do TextField "Read-Only" por um Card de Informação */}
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 1.5,
                                        bgcolor: 'action.hover',
                                        borderRadius: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Lucide.Building color={theme.palette.action.active} />
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                            lineHeight={1}
                                        >
                                            Sessão Atual (Cliente)
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            fontWeight="bold"
                                            color="text.primary"
                                        >
                                            {resolvedClientName
                                                ? resolvedClientName
                                                : 'Carregando...'}
                                        </Typography>
                                    </Box>
                                </Paper>

                                <Stack spacing={1}>
                                    <TextField
                                        label="Access Key IoT"
                                        fullWidth
                                        size="small"
                                        type={
                                            showAccessKey ? 'text' : 'password'
                                        }
                                        value={accessKey}
                                        onChange={(e) =>
                                            setAccessKey(e.target.value)
                                        }
                                        error={!!fieldErrors.accessKey}
                                        helperText={
                                            fieldErrors.accessKey ||
                                            'Chave necessária para vincular o BioAILab ao laboratório.'
                                        }
                                        disabled={sending || generatingKey}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() =>
                                                            setShowAccessKey(
                                                                (f) => !f
                                                            )
                                                        }
                                                        disabled={sending}
                                                        aria-label={
                                                            showAccessKey
                                                                ? 'Ocultar Access Key'
                                                                : 'Mostrar Access Key'
                                                        }
                                                    >
                                                        {showAccessKey ? (
                                                            <Lucide.EyeOff />
                                                        ) : (
                                                            <Lucide.Eye />
                                                        )}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <Box
                                        display="flex"
                                        justifyContent={{
                                            xs: 'center',
                                            sm: 'flex-end',
                                        }}
                                    >
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            size="small"
                                            fullWidth={isMobile}
                                            startIcon={
                                                generatingKey ? (
                                                    <CircularProgress
                                                        size={16}
                                                        color="inherit"
                                                    />
                                                ) : (
                                                    <Lucide.Key />
                                                )
                                            }
                                            onClick={handleGenerateKey}
                                            disabled={sending || generatingKey}
                                            sx={{
                                                borderRadius: 2,
                                                textTransform: 'none',
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            Gerar Nova Chave
                                        </Button>
                                    </Box>
                                </Stack>

                                <Divider sx={{ my: { xs: 0.5, sm: 1 } }} />

                                {device && (
                                    <Box
                                        sx={{
                                            bgcolor: 'success.50',
                                            border: '1px solid',
                                            borderColor: 'success.light',
                                            borderRadius: 2,
                                            p: 1.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                        }}
                                    >
                                        <Lucide.BluetoothConnected color={theme.palette.success.main} />
                                        <Box>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                display="block"
                                                lineHeight={1}
                                            >
                                                Conectado via Bluetooth
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                fontWeight="bold"
                                                sx={{
                                                    color: (t) =>
                                                        t.palette.mode === 'dark'
                                                            ? t.palette.success.light
                                                            : t.palette.success.dark,
                                                }}
                                            >
                                                {device.name || device.id}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}

                                <Autocomplete
                                    options={networks}
                                    autoHighlight
                                    size="small"
                                    getOptionLabel={(option) =>
                                        option.ssid || ''
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                        option.ssid === value.ssid
                                    }
                                    value={selectedNetwork}
                                    onChange={(_, newValue) => {
                                        setSelectedNetwork(newValue);
                                        setOpenAC(false);
                                    }}
                                    open={
                                        openAC &&
                                        (scanning || networks.length > 0)
                                    }
                                    onOpen={() => setOpenAC(true)}
                                    onClose={() => setOpenAC(false)}
                                    disabled={sending}
                                    loading={scanning}
                                    loadingText={
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={1}
                                            p={1}
                                        >
                                            <CircularProgress size={16} />
                                            <Typography variant="body2">
                                                A procurar redes próximas...
                                            </Typography>
                                        </Stack>
                                    }
                                    noOptionsText="Nenhuma rede encontrada"
                                    PaperComponent={(props) => (
                                        <Paper
                                            {...props}
                                            elevation={4}
                                            sx={{ borderRadius: 2, mt: 0.5 }}
                                        />
                                    )}
                                    renderOption={(props, option) => {
                                        const { key, ...itemProps } = props;
                                        const rssiColor = getSignalColor(
                                            option.rssi
                                        );
                                        const semanticColor =
                                            getSignalColorSemantic(option.rssi);

                                        return (
                                            <Box
                                                component="li"
                                                key={option.ssid}
                                                {...itemProps}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    py: { xs: 1, sm: 1.5 },
                                                    px: 2,
                                                    gap: 1.5,
                                                    borderBottom: '1px solid',
                                                    borderColor: 'divider',
                                                    transition:
                                                        'background-color 0.2s',
                                                    '&:hover': {
                                                        bgcolor: 'action.hover',
                                                    },
                                                    '&:last-child': {
                                                        borderBottom: 'none',
                                                    },
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        color: rssiColor,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    {getSignalIcon(option.rssi)}
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 600,
                                                        flexGrow: 1,
                                                        color: 'text.primary',
                                                        wordBreak: 'break-word',
                                                        whiteSpace: 'normal',
                                                        lineHeight: 1.3,
                                                        fontSize: isMobile
                                                            ? '0.85rem'
                                                            : '1rem',
                                                    }}
                                                >
                                                    {option.ssid}
                                                </Typography>
                                                <Chip
                                                    label={`${option.rssi} dBm`}
                                                    size="small"
                                                    variant="outlined"
                                                    color={semanticColor}
                                                    sx={{
                                                        height: 24,
                                                        fontSize: '0.75rem',
                                                        fontWeight: 'bold',
                                                        display: {
                                                            xs: 'none',
                                                            sm: 'inline-flex',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        );
                                    }}
                                    renderInput={(params) => {
                                        const inputProps = {
                                            ...params.InputProps,
                                        };

                                        return (
                                            <TextField
                                                {...params}
                                                label="Rede Wi-Fi (SSID)"
                                                fullWidth
                                                size="small"
                                                error={!!fieldErrors.ssid}
                                                helperText={fieldErrors.ssid}
                                                InputProps={{
                                                    ...inputProps,
                                                    startAdornment: (
                                                        <>
                                                            <InputAdornment
                                                                position="start"
                                                                sx={{
                                                                    pl: 1,
                                                                    mr: -0.5,
                                                                }}
                                                            >
                                                                {selectedNetwork ? (
                                                                    <Lucide.Wifi color={theme.palette.success.main} />
                                                                ) : (
                                                                    <Lucide.Wifi color={theme.palette.action.active} />
                                                                )}
                                                            </InputAdornment>
                                                            {
                                                                inputProps.startAdornment
                                                            }
                                                        </>
                                                    ),
                                                    endAdornment: (
                                                        <>
                                                            {
                                                                inputProps.endAdornment
                                                            }
                                                            <Divider
                                                                orientation="vertical"
                                                                flexItem
                                                                sx={{
                                                                    mx: 1,
                                                                    my: 0.5,
                                                                }}
                                                            />
                                                            <Tooltip
                                                                title={
                                                                    scanning
                                                                        ? 'Procurando redes...'
                                                                        : 'Buscar redes Wi-Fi próximas'
                                                                }
                                                            >
                                                                <Box
                                                                    display="flex"
                                                                    alignItems="center"
                                                                >
                                                                    <Button
                                                                        variant="text"
                                                                        color="primary"
                                                                        onClick={
                                                                            handleScan
                                                                        }
                                                                        disabled={
                                                                            sending ||
                                                                            scanning
                                                                        }
                                                                        size="small"
                                                                        startIcon={
                                                                            scanning ? (
                                                                                <CircularProgress
                                                                                    size={
                                                                                        16
                                                                                    }
                                                                                    color="inherit"
                                                                                />
                                                                            ) : (
                                                                                <Lucide.Search />
                                                                            )
                                                                        }
                                                                        sx={{
                                                                            display:
                                                                                {
                                                                                    xs: 'none',
                                                                                    sm: 'inline-flex',
                                                                                },
                                                                            textTransform:
                                                                                'none',
                                                                            fontWeight:
                                                                                'bold',
                                                                            px: 1,
                                                                            minWidth:
                                                                                'auto',
                                                                        }}
                                                                    >
                                                                        Buscar
                                                                    </Button>

                                                                    <IconButton
                                                                        edge="end"
                                                                        color="primary"
                                                                        onClick={
                                                                            handleScan
                                                                        }
                                                                        disabled={
                                                                            sending ||
                                                                            scanning
                                                                        }
                                                                        size="small"
                                                                        sx={{
                                                                            display:
                                                                                {
                                                                                    xs: 'inline-flex',
                                                                                    sm: 'none',
                                                                                },
                                                                            mr: 0.5,
                                                                        }}
                                                                    >
                                                                        {scanning ? (
                                                                            <CircularProgress
                                                                                size={
                                                                                    20
                                                                                }
                                                                                color="inherit"
                                                                            />
                                                                        ) : (
                                                                            <Lucide.Search />
                                                                        )}
                                                                    </IconButton>
                                                                </Box>
                                                            </Tooltip>
                                                        </>
                                                    ),
                                                }}
                                            />
                                        );
                                    }}
                                />

                                <TextField
                                    label="Senha da Rede"
                                    fullWidth
                                    size="small"
                                    type={showPwd ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) =>
                                        setPassword(e.target.value)
                                    }
                                    error={!!fieldErrors.password}
                                    helperText={fieldErrors.password}
                                    disabled={sending}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    edge="end"
                                                    onClick={() =>
                                                        setShowPwd((f) => !f)
                                                    }
                                                    aria-label={
                                                        showPwd
                                                            ? 'Ocultar senha'
                                                            : 'Mostrar senha'
                                                    }
                                                >
                                                    {showPwd ? (
                                                        <Lucide.EyeOff />
                                                    ) : (
                                                        <Lucide.Eye />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <Box
                                    sx={{
                                        bgcolor: 'action.hover',
                                        p: { xs: 1, sm: 1.5 },
                                        borderRadius: 2,
                                    }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={includeCode}
                                                onChange={(e) =>
                                                    setIncludeCode(
                                                        e.target.checked
                                                    )
                                                }
                                                disabled={sending}
                                                color="primary"
                                                size="small"
                                            />
                                        }
                                        label={
                                            <Tooltip title="Ao ativar, o dispositivo enviará estas credenciais para os outros dispositivos BioAILab conectados na mesma rede.">
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="500"
                                                    fontSize={
                                                        isMobile
                                                            ? '0.85rem'
                                                            : '0.875rem'
                                                    }
                                                >
                                                    Propagar credenciais (BLE
                                                    Mesh)?
                                                </Typography>
                                            </Tooltip>
                                        }
                                        sx={{ m: 0 }}
                                    />
                                </Box>

                                <Stack
                                    direction={{
                                        xs: 'column-reverse',
                                        sm: 'row',
                                    }}
                                    spacing={{ xs: 1.5, sm: 2 }}
                                    justifyContent="space-between"
                                    pt={1}
                                >
                                    <Button
                                        onClick={back}
                                        disabled={sending}
                                        color="inherit"
                                        size="medium"
                                        fullWidth={isMobile}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        ← Voltar e Desconectar
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="medium"
                                        fullWidth={isMobile}
                                        startIcon={<Lucide.Send />}
                                        disabled={
                                            sending ||
                                            bootLoading ||
                                            !resolvedClientId
                                        }
                                        onClick={handleSend}
                                        sx={{
                                            borderRadius: 2,
                                            px: { sm: 4 },
                                            py: { xs: 1, sm: 1 },
                                        }}
                                    >
                                        {sending
                                            ? 'A Provisionar...'
                                            : 'Provisionar'}
                                    </Button>
                                </Stack>
                            </Stack>
                        )}
                    </Stack>
                </CardContent>
            </Card>

            {/* MODAL DE STATUS DE ENVIO */}
            <Dialog
                open={modalOpen}
                fullWidth
                maxWidth="sm"
                onClose={() => !sending && setModalOpen(false)}
                PaperProps={{ sx: { borderRadius: 3, m: { xs: 2, sm: 4 } } }}
            >
                <DialogTitle>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Typography variant="h6" fontWeight="bold">
                            Status do Provisionamento
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => !sending && setModalOpen(false)}
                            disabled={sending}
                        >
                            <Lucide.X />
                        </IconButton>
                    </Box>
                </DialogTitle>
                {sending && <LinearProgress color="primary" />}

                <DialogContent
                    dividers
                    sx={{
                        p: 0,
                        maxHeight: 350,
                        overflowY: 'auto',
                        bgcolor: 'background.default',
                    }}
                >
                    <List dense disablePadding>
                        {statusLog.map((entry, i) => {
                            const color =
                                entry.level === 'error'
                                    ? 'error.main'
                                    : entry.level === 'warning'
                                      ? 'warning.main'
                                      : entry.level === 'success'
                                        ? theme.palette.mode === 'dark'
                                            ? theme.palette.success.light
                                            : theme.palette.success.dark
                                        : 'text.primary';
                            return (
                                <ListItem
                                    key={i}
                                    divider
                                    sx={{
                                        py: 1.5,
                                        px: 3,
                                        borderColor: 'divider',
                                    }}
                                >
                                    <ListItemText
                                        primary={
                                            <Typography
                                                sx={{
                                                    color,
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.85rem',
                                                    fontWeight: 500,
                                                }}
                                            >
                                                {entry.msg}
                                            </Typography>
                                        }
                                        secondary={
                                            <Typography
                                                sx={{
                                                    color: 'text.secondary',
                                                    fontSize: '0.7rem',
                                                    mt: 0.5,
                                                }}
                                            >
                                                {entry.time.toLocaleTimeString()}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                </DialogContent>
                <DialogActions
                    sx={{
                        p: 2,
                        bgcolor: 'background.paper',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 1,
                    }}
                >
                    {provisioningComplete ? (
                        <>
                            <Button
                                onClick={() => {
                                    setModalOpen(false);
                                    setStep(0);
                                    setNetworks([]);
                                    setSelectedNetwork(null);
                                    setPassword('');
                                    setAccessKey('');
                                }}
                                color="inherit"
                                fullWidth={isMobile}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                }}
                            >
                                Provisionar outro BioAILab
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                fullWidth={isMobile}
                                sx={{
                                    borderRadius: 2,
                                    ml: { sm: 'auto' },
                                    m: { xs: 0 },
                                }}
                                onClick={() =>
                                    navigate(`/${resolvedTenant}`)
                                }
                            >
                                Concluir e Sair
                            </Button>
                        </>
                    ) : statusLog.some((l) => l.level === 'error') &&
                      !sending ? (
                        <Button
                            startIcon={<Lucide.RotateCcw />}
                            variant="outlined"
                            color="error"
                            fullWidth={isMobile}
                            onClick={() => setModalOpen(false)}
                        >
                            Tentar Novamente
                        </Button>
                    ) : (
                        <Button
                            onClick={() => setModalOpen(false)}
                            disabled={sending}
                            color="inherit"
                            fullWidth={isMobile}
                        >
                            Fechar Janela
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* MODAL DE AJUDA / FLUXOGRAMA DE PROVISIONAMENTO */}
            <Dialog
                open={helpOpen}
                onClose={() => setHelpOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: 3, m: 2 } }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Box display="flex" alignItems="center" gap={1}>
                            <Lucide.HelpCircle color={theme.palette.primary.main} />
                            <Typography variant="h6" fontWeight="bold">
                                Como Funciona o Provisionamento?
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            onClick={() => setHelpOpen(false)}
                        >
                            <Lucide.X />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers sx={{ p: { xs: 2, sm: 4 } }}>
                    <Typography variant="body2" color="text.secondary" mb={3}>
                        Siga o fluxo abaixo para vincular um novo BioAILab ao
                        seu laboratório de forma segura.
                    </Typography>

                    <Stepper orientation="vertical">
                        <Step active={true}>
                            <StepLabel
                                StepIconComponent={() => (
                                    <Lucide.Bluetooth color={theme.palette.primary.main} />
                                )}
                            >
                                <Typography fontWeight="bold">
                                    1. Conectar via Bluetooth (BLE)
                                </Typography>
                            </StepLabel>
                            <StepContent>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Aproxime-se do BioAILab e clique em{' '}
                                    <b>"Conectar Dispositivo"</b>. O navegador
                                    solicitará permissão para emparelhar com
                                    equipamentos prefixados com{' '}
                                    <code>AUFTEK_</code>.
                                </Typography>
                            </StepContent>
                        </Step>

                        <Step active={true}>
                            <StepLabel
                                StepIconComponent={() => (
                                    <Lucide.KeyRound color={theme.palette.secondary.main} />
                                )}
                            >
                                <Typography fontWeight="bold">
                                    2. Gerar Access Key IoT
                                </Typography>
                            </StepLabel>
                            <StepContent>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Após conectar, clique no botão{' '}
                                    <b>"Gerar Nova Chave"</b>. Esta chave
                                    criptografada é gerada pelo servidor e serve
                                    para atrelar fisicamente o BioAILab ao seu
                                    Cliente atual.
                                </Typography>
                            </StepContent>
                        </Step>

                        <Step active={true}>
                            <StepLabel
                                StepIconComponent={() => (
                                    <Lucide.Rss color={theme.palette.primary.main} />
                                )}
                            >
                                <Typography fontWeight="bold">
                                    3. Buscar e Configurar Wi-Fi
                                </Typography>
                            </StepLabel>
                            <StepContent>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Clique no botão <b>"Buscar"</b> (Lupa) no
                                    campo do Wi-Fi. O BioAILab irá procurar as
                                    redes ao redor e enviar para a tela.
                                    Selecione sua rede local e insira a senha.
                                </Typography>
                            </StepContent>
                        </Step>

                        <Step active={true}>
                            <StepLabel
                                StepIconComponent={() => (
                                    <Lucide.CheckCheck color={theme.palette.success.main} />
                                )}
                            >
                                <Typography fontWeight="bold">
                                    4. Provisionar
                                </Typography>
                            </StepLabel>
                            <StepContent>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Clique em <b>"Provisionar"</b>. Os dados
                                    serão encriptados no seu navegador e
                                    enviados via Bluetooth para o BioAILab, que
                                    se conectará à nuvem automaticamente.
                                </Typography>
                            </StepContent>
                        </Step>
                    </Stepper>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Button
                        onClick={() => setHelpOpen(false)}
                        variant="contained"
                        fullWidth={isMobile}
                    >
                        Entendi
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!errorMsg}
                autoHideDuration={6000}
                onClose={() => setErrorMsg('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity="error"
                    variant="filled"
                    onClose={() => setErrorMsg('')}
                    sx={{ borderRadius: 2, width: '100%' }}
                >
                    {errorMsg}
                </Alert>
            </Snackbar>
        </Container>
    );
}
